import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import {
  LineChart, Line, Tooltip, ResponsiveContainer,
} from 'recharts';
import { higginsColors } from '@higgins/ui';
import type { KpiData, ChartFormat } from '@/lib/types';

interface KpiCardProps {
  data: KpiData;
}

function fmtValue(v: number, fmt: ChartFormat): string {
  if (fmt === 'currency') {
    const abs = Math.abs(v);
    const sign = v < 0 ? '-' : '';
    if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
    if (abs >= 1_000)     return `${sign}$${(abs / 1_000).toFixed(1)}K`;
    return `${sign}$${abs.toFixed(0)}`;
  }
  if (fmt === 'percent') return `${(v * 100).toFixed(1)}%`;
  if (fmt === 'decimal') return v.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function fmtDelta(actual: number, target: number, invert?: boolean): { pct: string; positive: boolean | null } {
  if (!target) return { pct: '–', positive: null };
  const delta = (actual - target) / Math.abs(target);
  return {
    pct: `${delta >= 0 ? '+' : ''}${(delta * 100).toFixed(1)}%`,
    positive: invert ? delta <= 0 : delta >= 0,
  };
}

function SparkTooltip({ active, payload, fmt }: {
  active?: boolean;
  payload?: { value: number; dataKey?: string; payload: { month?: string } }[];
  fmt: ChartFormat;
}) {
  if (!active || !payload?.length) return null;
  const row    = payload[0].payload;
  const actual = payload.find(p => p.dataKey === 'v')?.value;
  const budget = payload.find(p => p.dataKey === 'budget')?.value;
  return (
    <div className="bg-white border border-border rounded shadow-sm px-2 py-1 text-xs space-y-0.5">
      {row.month && <p className="text-muted-foreground">{row.month}</p>}
      {actual != null && (
        <p className="flex items-center gap-1.5 font-semibold">
          <span className="inline-block w-2.5 h-0.5" style={{ background: higginsColors[0] }} />
          {fmtValue(actual, fmt)}
        </p>
      )}
      {budget != null && (
        <p className="flex items-center gap-1.5 text-muted-foreground">
          <span className="inline-block w-2.5 border-t border-dashed" style={{ borderColor: higginsColors[1] }} />
          Budget: {fmtValue(budget, fmt)}
        </p>
      )}
    </div>
  );
}

export function KpiCard({ data }: KpiCardProps) {
  const { label, value, format = 'number', comparison, sparkline, invertComparison } = data;
  const sparkValues = sparkline?.data ?? [];
  const sparkLabels = sparkline?.labels ?? [];
  const sparkTarget = sparkline?.target ?? [];
  const hasBudgetLine = sparkTarget.length === sparkValues.length && sparkTarget.length >= 2;

  // Build recharts-compatible data array — include label directly so tooltip can read it,
  // and the per-month target so an optional budget line can overlay the actual trend (#21).
  const chartData = sparkValues.map((v, i) => ({ v, budget: sparkTarget[i], month: sparkLabels[i] ?? '' }));

  const delta = comparison != null ? fmtDelta(value, comparison.value, invertComparison) : null;
  const TrendIcon = !delta ? null
    : delta.positive === true  ? TrendingUp
    : delta.positive === false ? TrendingDown
    : Minus;

  const trendColor = delta?.positive === true  ? '#93b856'
    : delta?.positive === false ? '#c56055'
    : '#6b7280';

  return (
    <div className="h-full flex flex-col p-3">
      {/* KPI data — centred in remaining space above sparkline */}
      <div className="flex-1 flex flex-col items-center justify-center text-center min-h-0">
        <p className="text-xs font-medium text-muted-foreground truncate w-full">{label}</p>
        <p
          className="mt-1 text-2xl font-bold tabular-nums"
          style={{ color: delta ? trendColor : undefined }}
        >
          {fmtValue(value, format)}
        </p>
        {comparison != null && (
          <div className="mt-1 space-y-0.5">
            <p className="text-xs text-muted-foreground">
              Goal: {fmtValue(comparison.value, format)}
            </p>
            {delta && TrendIcon && (
              <div className="flex items-center justify-center gap-1" style={{ color: trendColor }}>
                <TrendIcon className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="text-xs font-semibold">({delta.pct})</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sparkline — fixed height at bottom, never clipped */}
      {chartData.length >= 2 && (
        <div className="flex-shrink-0 h-10 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <Line
                type="monotone"
                dataKey="v"
                stroke={higginsColors[0]}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 3, fill: higginsColors[0] }}
              />
              {hasBudgetLine && (
                <Line
                  type="monotone"
                  dataKey="budget"
                  stroke={higginsColors[1]}
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                  dot={false}
                  activeDot={false}
                />
              )}
              <Tooltip
                content={({ active, payload }) => (
                  <SparkTooltip
                    active={active}
                    payload={payload as unknown as { value: number; payload: { month: string } }[]}
                    fmt={format}
                  />
                )}
                cursor={{ stroke: '#e5e7eb', strokeWidth: 1 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
